import React, { useEffect, useRef } from "react";
import { container } from "@/utils/mobx";
import { useInputEvent } from "@/components/toolkit/InputProvider";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import { PumlPreviewStore } from "./store";
import "./s.puml_preview.css";

export const PumlPreview = container((props: {
    store: PumlPreviewStore;
    isPuml: boolean;
    content: string;
    active: boolean;
}) => {
    const store = props.store;
    const previewRef = useRef<HTMLDivElement | null>(null);
    const pinchStartScaleRef = useRef(1);

    useEffect(() => {
        const el = previewRef.current;
        if (!el || !store.svg) return;
        el.scrollTop = window.innerHeight * 0.5;
        el.scrollLeft = window.innerWidth * 0.5;
    }, [store.svg]);

    useInputEvent('ctrl_wheel', (e) => {
        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        store.setScale(Math.max(0.25, Math.min(8, store.scale * factor)));
        return true;
    }, [store], { target: previewRef });

    useInputEvent('pinch', (e) => {
        if (e.phase === 'start') {
            pinchStartScaleRef.current = store.scale;
        } else if (e.phase === 'change') {
            store.setScale(Math.max(0.25, Math.min(8, pinchStartScaleRef.current * e.scale)));
        }
    }, [store], { target: previewRef });

    useInputEvent('scroll', (e) => {
        const el = previewRef.current;
        if (!el || e.phase !== 'change') return;
        el.scrollLeft += e.dx;
        el.scrollTop += e.dy;
        return true;
    }, [], { target: previewRef, inertia: true });

    useEffect(() => {
        const el = previewRef.current;
        if (!el) return;
        el.style.cursor = 'grab';
        return () => { el.style.cursor = ''; };
    }, []);

    const dragActiveRef = useRef(false);
    useInputEvent('drag', (e) => {
        const el = previewRef.current;
        if (!el || e.button !== 0) return;
        if (e.phase === 'start') {
            dragActiveRef.current = el.contains(e.startTarget);
            if (dragActiveRef.current) {
                el.style.cursor = 'grabbing';
                el.style.userSelect = 'none';
            }
        } else if (dragActiveRef.current) {
            if (e.phase === 'change') {
                el.scrollLeft -= e.dx;
                el.scrollTop -= e.dy;
            } else {
                dragActiveRef.current = false;
                el.style.cursor = 'grab';
                el.style.userSelect = '';
            }
        }
    }, []);

    return (
        <div ref={previewRef} className={`puml_preview ${SWIPE_GUARD_CLASS}`}>
            {store.svg === null ? (
                <div className="puml_preview__loading">
                    <div className="puml_preview__loading_spinner" />
                </div>
            ) : store.svg === "" ? (
                <div className="puml_preview__error">Failed to render diagram</div>
            ) : (
                <div style={{ padding: '50vh 50vw' }}>
                    <div style={{ zoom: store.scale }} dangerouslySetInnerHTML={{ __html: store.svg }} />
                </div>
            )}
        </div>
    );
}, PumlPreviewStore);
